import { DataSource } from 'app/components/data/DataSource'
import { debounce, ObjectParser } from 'app/components/data/utils'
import { IMaterialSelectOption, MaterialInput, MaterialSelect } from 'app/components/material'
import * as React from 'react'
import * as BS from 'react-bootstrap'
const style = require('./style.less')

export interface IDataGridProps {
  columns: Array<IColumn<any>>
  pageSizes: number[],
  DataSource: DataSource<any>
  hideInputs?: boolean
  className?: string
  tableHeaderClassName?: string
}

export interface IDataGridState {
  numOfPages: number
  currentPage: number
  pageOptions: IMaterialSelectOption[]
  pageSize: number
  pageSizeOptions: IMaterialSelectOption[]
  data: any[]
  dataSize: number
  searchWord: string
}

interface IColumn<T> {
  label: string
  pathExpression?: string
  customRender?: (data: T, col: IColumn<T>) => JSX.Element
  sortBy?: string
  searchable?: boolean
  sortable?: boolean
}

export class DataGrid extends React.Component<IDataGridProps, IDataGridState> {
  state = {
    numOfPages: 0,
    currentPage: 0,
    pageOptions: [{ name: 'Loading...', value: '' }],
    pageSize: 0,
    pageSizeOptions: [],
    data: [],
    dataSize: 0,
    searchWord: ''
  }

  private unlistenToDataSource: () => void
  private updateSearchWordDebounce = debounce((searchWord) => {
    const currentPage = 0
    const searchPathExpressions = this.props.columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.pathExpression)

    this.setState({ searchWord, currentPage })

    searchWord = searchWord.replace(/\,/g, '') //Remove commas from search for numbers

    this.props.DataSource
      .setStartIndexAndSize(0, this.state.pageSize)
      .setSearch(searchWord, searchPathExpressions)
      .sync()

    return Promise.resolve()
  })

  componentWillMount() {
    const pageSize = this.props.pageSizes[0]

    this.setState({ pageSize })
    this.updatePageSizeOptions()

    this.unlistenToDataSource = this.props.DataSource.onSync((data, dataSize) => {
      this.setState({ data, dataSize }, () => {
        this.updatePageOptions()
      })
    })

    this.props.DataSource
      .setStartIndexAndSize(0, pageSize)
      .sync()
  }

  componentWillUnmount() {
    this.unlistenToDataSource()
  }

  updatePageSizeOptions() {
    const pageSizeOptions: IMaterialSelectOption[] = []

    for (const option of this.props.pageSizes) {
      pageSizeOptions.push(
        { name: `Display ${option}`, value: String(option) }
      )
    }

    this.setState({ pageSizeOptions })
  }

  updatePageOptions() {
    const numOfPages = Math.ceil(this.state.dataSize / this.state.pageSize)
    const pageOptions: IMaterialSelectOption[] = []

    if (numOfPages > 0) {
      for (let i = 0; i < numOfPages; i++) {
        pageOptions.push(
          { name: `${i + 1} of ${numOfPages}`, value: String(i) }
        )
      }
    } else {
      pageOptions.push({ name: 'No Records Found', value: '' })
    }

    this.setState({ pageOptions, numOfPages })
  }

  updatePageSize(sizeIndexStr: string | number) {
    const pageSize = Number(sizeIndexStr)
    const currentPage = 0

    this.setState({ pageSize, currentPage })

    this.props.DataSource
      .setStartIndexAndSize(0, pageSize)
      .sync()
  }

  updateCurrentPage(pageIndex: string | number) {
    const currentPage = Number(pageIndex)
    const newStartIndex = currentPage * this.state.pageSize

    this.setState({ currentPage })

    this.props.DataSource
      .setStartIndexAndSize(newStartIndex, this.state.pageSize)
      .sync()
  }

  renderCell(row: any, col: IColumn<any>) {
    let value = ObjectParser.QueryAndGetFirst<any>(row, col.pathExpression)
    const dangerHTML = { __html: (value != null) ? value.toLocaleString() : null }
    const searchWord = this.state.searchWord
    const searchable = col.searchable !== false

    if (searchable && searchWord) {

      //Convert Number to Locale string
      if (value && !isNaN(value)) {
        value = Number(value).toLocaleString()
      }

      const valueStr = String(value)

      //searchRegexStr allows for the searchWord to have commas anywhere inside of it which allows for better string search within numbers
      const searchRegexStr = String(searchWord)
        .replace(/\,/g, '') //-----Remove existing commas
        .split('')//---------------Create array of characters
        .map((l) => `${l}\\,?`)//--Map character to string regex with a optional, tailing comma: ex. 'a' -> 'a\\,?'
        .join('')//----------------Join the character array back to a string

      const searchRegex = new RegExp(`(${searchRegexStr})`, 'ig')

      dangerHTML.__html = valueStr.replace(searchRegex, '<span class="highlight">$1</span>')
    }

    return dangerHTML
  }

  updateSort(col: IColumn<any>) {
    const newIndex = col.pathExpression

    if (col.sortable === false) {
      return
    }

    const sortIndex = this.props.DataSource.getSortIndex()
    const sortOrder = this.props.DataSource.getSortOrder()
    const sortBy = col.sortBy
    const sameIndexClicked = newIndex === sortIndex
    let newSortOrder = sortOrder ? sortOrder : 'desc'

    if (sameIndexClicked) {
      newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc'
    }

    this.setState({ currentPage: 0 })

    this.props.DataSource
      .setStartIndexAndSize(0, this.state.pageSize)
      .setSorting(newIndex, newSortOrder, sortBy)
      .sync()
  }

  updateSearchWord(searchWord) {
    if (this.state.searchWord === searchWord) {
      return
    }

    this.setState({ searchWord })

    this.updateSearchWordDebounce(searchWord)
  }

  clearParameters() {
    const pageSize = this.props.pageSizes[0]
    const currentPage = 0
    const searchWord = ''

    this.setState({ currentPage, pageSize, searchWord })

    this.props.DataSource
      .setStartIndexAndSize(0, pageSize)
      .clearSearch()
      .clearSorting()
      .sync()
  }

  render() {
    const sortIndex = this.props.DataSource.getSortIndex()
    const sortOrder = this.props.DataSource.getSortOrder()
    const sortOrderStyle = sortOrder === 'desc' ? style.desc : style.asc
    const tableHeaderClassName = this.props.tableHeaderClassName

    return (
      <div className={this.props.className}>
        <section className={style.dataGrid}>
          <div className="panel">
            {this.props.hideInputs ?
              null :
              <div className="panel-top">
                <BS.Col sm={4} data-hidden-placeholder="true">
                  <BS.InputGroup>
                    <div className="input-group-addon">
                      <i className="fa fa-search"></i>
                    </div>
                    <MaterialInput
                      placeholder="Search"
                      disableMaterialPlaceholder={true}
                      value={this.state.searchWord}
                      onChange={(searchWord) => this.updateSearchWord(searchWord)}
                    />
                  </BS.InputGroup>
                </BS.Col>
                <BS.Col sm={3}>
                  <MaterialSelect
                    value={String(this.state.currentPage)}
                    options={this.state.pageOptions}
                    onChange={(index) => this.updateCurrentPage(index)}
                    disableMaterialPlaceholder={true}
                  />
                </BS.Col>
                <BS.Col sm={3}>
                  <MaterialSelect
                    options={this.state.pageSizeOptions}
                    value={String(this.state.pageSize)}
                    onChange={(sizeStr) => this.updatePageSize(sizeStr)}
                    disableMaterialPlaceholder={true}
                  />
                </BS.Col>
                <BS.Col sm={2} style={{ padding: 0 }} className="hidden-xs">
                  <BS.DropdownButton id="datagrid-dropdown" title="More " noCaret pullRight>
                    <BS.MenuItem header>Actions</BS.MenuItem>
                    <BS.MenuItem onClick={() => this.clearParameters()}>
                      Reset Sort & Search
                    </BS.MenuItem>
                  </BS.DropdownButton>
                </BS.Col>
              </div>
            }

            <div className="table-responsive">
              <table className={`table table-striped table-hover ${style.noMarginBottom}`} >
                <thead>
                  <tr>
                    {this.props.columns.map((col) => {
                      const isSortable = col.sortable !== false
                      const isSortedCol = sortIndex === col.pathExpression
                      let className = isSortedCol ? sortOrderStyle : ''
                      className += isSortable ? ` ${style.sortable}` : ''
                      className += ' ' + tableHeaderClassName
                      return (
                        <th
                          key={col.label}
                          className={className}
                          onClick={() => this.updateSort(col)}>
                          {col.label}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(this.state.data) ? this.state.data.map((row, rowIndex) =>
                    <tr key={rowIndex}>
                      {this.props.columns.map((col) => {
                        if (col.customRender) {
                          return (
                            <td key={`${rowIndex}${col.label}`}>
                              {col.customRender(row, col)}
                            </td>
                          )
                        }

                        return (
                          <td
                            key={`${rowIndex}${col.pathExpression}`}
                            dangerouslySetInnerHTML={this.renderCell(row, col)}>
                          </td>
                        )
                      })}
                    </tr>
                  ) : <i className="fa fa-spinner fa-spin" style={{ fontSize: '48px', color: '#fff' }}></i>}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/app/components/data/DataGrid/index.tsx